import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "../../lib/utils.js";

// The repo previously hand-rolled ~20 `position:fixed` overlays, none of which
// trapped focus, closed on Escape, or set aria-modal. Radix gives all three.

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-100 bg-black/40 backdrop-blur-[1px]", className)}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Hides the built-in close button for flows that must be dismissed deliberately. */
  hideClose?: boolean;
  /**
   * Extra classes for the backdrop. Needed when the dialog opens over another
   * portal with a higher z-index (e.g. a full-screen drawer) so the backdrop,
   * like the content, can be raised above it.
   */
  overlayClassName?: string;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideClose = false, overlayClassName, onInteractOutside, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={ref}
      onInteractOutside={(e) => {
        onInteractOutside?.(e);
        // A ConfirmDialog opened from inside this dialog (AsyncButton's
        // `confirm`) renders in its own portal, so pressing its Confirm or
        // Cancel counts as "outside". Without this, answering the card would
        // also dismiss the dialog underneath -- and with it whatever the user
        // had typed.
        if ((e.target as HTMLElement | null)?.closest?.('[role="alertdialog"]')) e.preventDefault();
        // Same for a DropdownSelect / DropdownPanel opened inside the dialog: its panel is
        // portalled to <body>, so picking an option would otherwise close the dialog.
        if ((e.target as HTMLElement | null)?.closest?.("[data-dropdown-panel]")) e.preventDefault();
      }}
      className={cn(
        "fixed top-1/2 left-1/2 z-100 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4",
        "max-h-[85vh] overflow-y-auto rounded-lg border border-surface-200 bg-surface-0 p-5 shadow-xl",
        className
      )}
      {...props}
    >
      {children}
      {!hideClose && (
        <DialogPrimitive.Close
          className="absolute top-4 right-4 rounded-sm text-surface-400 transition-colors hover:text-surface-700 focus:ring-2 focus:ring-brand-500 focus:outline-none"
          aria-label="Close"
        >
          <X size={16} />
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-col gap-1 pr-8", className)} {...props} />;
}

function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-wrap justify-end gap-2", className)} {...props} />;
}

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn("text-[15px] font-bold", className)} {...props} />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-[13px] text-surface-500", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogTrigger,
  DialogClose,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
